import React from 'react';

import { Clear, DoneAll } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import { every, filter, forEach, isEmpty, map, some } from 'lodash';
import { CardLevel } from '../../../../components';
import { createScopedI18n } from '../../../../i18n/i18n';
import { AttendancesPageContext } from '../attendances_page_context';
import AttendanceCard from './attendance_card';

import type { JobHiringAttendance } from '../type';

const attendancesGroupI18n = createScopedI18n('pages.attendances');

export type AttendancesGroupProps = {
  title?: string;
  attendances: JobHiringAttendance[];
  disableSelectAll?: boolean;
};

type AttendancesGroupInnerProps = AttendancesGroupProps & {
  allSelected?: boolean;
  onSelectAttendance: (attendanceId: string, selected: boolean) => void;
};

export const AttendancesGroup = React.memo(
  ({ title, attendances, disableSelectAll, allSelected, onSelectAttendance }: AttendancesGroupInnerProps) => {
    const selectableAttendanceIds = React.useMemo(
      () =>
        map(
          filter(attendances, ({ status }) => status === 'pending'),
          'id',
        ),
      [attendances],
    );

    const onSelectAll = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        forEach(selectableAttendanceIds, (attendanceId) => {
          onSelectAttendance(attendanceId, !allSelected);
        });
      },
      [allSelected, onSelectAttendance, selectableAttendanceIds],
    );

    return (
      <Stack gap={1}>
        <Stack gap={0.5} flexDirection="row" alignItems="center">
          {title ? (
            <Typography variant="h6" fontWeight="normal" noWrap>
              {title}
            </Typography>
          ) : null}
          <div style={{ flex: 1 }} />
          <Button
            variant="outlined"
            size="small"
            disabled={disableSelectAll || isEmpty(selectableAttendanceIds)}
            startIcon={allSelected ? <Clear /> : <DoneAll />}
            onClick={onSelectAll}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {allSelected ? attendancesGroupI18n('deselect_all') : attendancesGroupI18n('select_all')}
          </Button>
        </Stack>

        <Stack gap={1} flexDirection="row" flexWrap="wrap">
          {isEmpty(attendances) ? (
            <CardLevel variant="outlined" level={1} sx={{ width: '100%' }}>
              <Stack justifyContent="center" alignItems="center" paddingX={2} padding={1}>
                <Typography variant="caption" textAlign="center" sx={{ opacity: 0.6 }}>
                  {attendancesGroupI18n('no_records')}
                </Typography>
              </Stack>
            </CardLevel>
          ) : (
            <>
              {map(attendances, (attendance) => (
                <AttendanceCard key={attendance.id} attendance={attendance} />
              ))}
            </>
          )}
        </Stack>
      </Stack>
    );
  },
);

const AttendancesGroupContainer = ({ title, attendances, disableSelectAll }: AttendancesGroupProps) => {
  const { selectedAttendancesIds, selectAttendance, canSelectAttendance } = React.useContext(AttendancesPageContext);
  const canSelectAny = React.useMemo(
    () => some(attendances, (attendance) => canSelectAttendance(attendance.id)),
    [attendances, canSelectAttendance],
  );
  const allSelected = React.useMemo(
    () => !isEmpty(attendances) && every(attendances, (attendance) => selectedAttendancesIds.has(attendance.id)),
    [attendances, selectedAttendancesIds],
  );

  return (
    <AttendancesGroup
      title={title}
      attendances={attendances}
      onSelectAttendance={selectAttendance}
      allSelected={allSelected}
      disableSelectAll={disableSelectAll ?? !canSelectAny}
    />
  );
};

export default AttendancesGroupContainer;
